import { api, toQuery } from "./client";
import type { Account, LogEntry, PaginatedAccounts, ProxyHistoryEntry, RunParams } from "../lib/types";

export interface AccountFilters {
  page?: number;
  per_page?: number;
  keyword?: string;
  task_status?: string;
  batch_no?: string;
  include_deleted?: boolean;
}

export function listAccounts(filters: AccountFilters) {
  return api<PaginatedAccounts>(`/api/v1/discord-accounts${toQuery(filters)}`);
}

export function listBatches(filters: { keyword?: string; limit?: number } = {}) {
  return api<{ batches: Array<{ batch_no: string; account_count: number }> }>(`/api/v1/discord-accounts/batches${toQuery(filters)}`);
}

export function updateAccount(id: number, patch: Partial<Account>) {
  return api<Account>(`/api/v1/discord-accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
}

export type DeletedImportStrategy = "keep_deleted" | "restore" | "skip";

export function importAccounts(payload: { text?: string; records?: Record<string, unknown>[]; batch_no?: string; batch_remark?: string; deleted_strategy?: DeletedImportStrategy }) {
  return api<{
    message: string;
    created: Account[];
    updated: Account[];
    skipped: Array<{ line: number; id: number; current_email: string; reason: string }>;
    errors: Array<{ line: number; message: string }>;
    batch_no: string;
  }>(
    "/api/v1/discord-accounts/import",
    { method: "POST", body: JSON.stringify(payload) }
  );
}

export function runAccountTask(id: number, taskType: string, params: RunParams) {
  return api(`/api/v1/discord-accounts/${id}/run/${taskType}`, {
    method: "POST",
    body: JSON.stringify({ params, force_new_proxy: Boolean(params.force_new_proxy) })
  });
}

export function refreshAccountProxy(id: number) {
  return api<{ ok: boolean; detail: string }>(`/api/v1/discord-accounts/${id}/proxy/refresh`, { method: "POST" });
}

export function getMfaCode(id: number) {
  return api<{ code: string; remaining_seconds: number }>(`/api/v1/discord-accounts/${id}/mfa-code`);
}

export function deleteAccount(id: number) {
  return api<{ message: string; account: Account }>(`/api/v1/discord-accounts/${id}`, { method: "DELETE" });
}

export function restoreAccount(id: number) {
  return api<{ message: string; account: Account }>(`/api/v1/discord-accounts/${id}/restore`, { method: "POST" });
}

export function listAccountRunLogs(id: number) {
  return api<{ logs: LogEntry[]; total: number }>(`/api/v1/discord-accounts/${id}/run-logs?per_page=200`);
}

export function listAccountChanges(id: number) {
  return api<{ logs: LogEntry[] }>(`/api/v1/discord-accounts/${id}/changes?limit=200`);
}

export function listAccountProxies(id: number) {
  return api<{ proxies: ProxyHistoryEntry[] }>(`/api/v1/discord-accounts/${id}/proxies?limit=50`);
}
